import { useMemo, useState } from "react";
import { FaChevronCircleLeft, FaChevronCircleRight } from "react-icons/fa";

import styles from "./ImageCarousel.module.css";
import ImageSlot from "./ImageSlot";

interface Props {
    editing?: boolean;
    images: { [key: string | number]: File | string | null };
    size?: number;
    width?: number;
    height?: number;
    onImageChange?: (index: number, file: File | null) => void;
}

export default function ImageCarousel({
    editing,
    images,
    size,
    width,
    height,
    onImageChange,
}: Props) {
    const [activeImage, setActiveImage] = useState(0);

    const getSize = useMemo(() => {
        if (size) return size;

        return Math.max(
            1,
            Object.keys(images).reduce<number>((prev, current) => {
                const isNumber = !isNaN(Number(current));
                return isNumber ? prev + 1 : prev;
            }, 0),
        );
    }, [size, images]);

    const viewerImages = useMemo(() => {
        if (editing) return images;

        return Object.entries(images)
            .filter(([key, _]) => !isNaN(Number(key)))
            .map(([_, val]) => val);
    }, [editing, images]);

    return (
        <div className={styles.carousel}>
            <ImageSlot
                image={viewerImages[activeImage]}
                width={width ?? 1040}
                height={height ?? 570}
                onImageChange={(img) => {
                    if (onImageChange) onImageChange(activeImage, img);
                }}
                editing={editing}
            />
            <div className={styles.carouselNav}>
                {editing &&
                    Array.from(
                        {
                            length: getSize,
                        },
                        (_, i) => {
                            return (
                                <button
                                    key={i}
                                    className={`${styles.carouselNavButton} ${activeImage === i ? styles.active : ""}`}
                                    onClick={() => setActiveImage(i)}
                                >
                                    {i + 1}
                                </button>
                            );
                        },
                    )}
                {!editing && (
                    <div className={styles.carouselIndicatorCollection}>
                        {Array.from(
                            {
                                length: getSize,
                            },
                            (_, i) => {
                                return (
                                    <span
                                        key={i}
                                        style={{
                                            display: "inline-block",
                                            width: "1.5em",
                                            lineHeight: "0.9rem",
                                            marginBottom: "0.15rem",
                                            cursor: "pointer",
                                        }}
                                        onClick={() => setActiveImage(i)}
                                    >
                                        {activeImage === i ? "⬤" : "◯"}
                                    </span>
                                );
                            },
                        )}
                    </div>
                )}
            </div>

            {getSize > 1 && (
                <>
                    <div
                        className={`${styles.carouselViewButton} ${styles.left}`}
                        onClick={() => {
                            setActiveImage((prev) =>
                                prev > 0 ? prev - 1 : getSize - 1,
                            );
                        }}
                    >
                        <FaChevronCircleLeft />
                    </div>
                    <div
                        className={`${styles.carouselViewButton} ${styles.right}`}
                        onClick={() => {
                            setActiveImage((prev) =>
                                prev < getSize - 1 ? prev + 1 : 0,
                            );
                        }}
                    >
                        <FaChevronCircleRight />
                    </div>
                </>
            )}
        </div>
    );
}
